import { redirect } from '@tanstack/react-router';
import type { AppRouterContext } from 'providers/AppRouter';

import { STRIP_COUPON } from 'router/search/opportunity';
import { getCouponEligibilityQueryOptions } from 'services/partnersSelfSignup';

import type { CouponEligibility } from 'services/partnersSelfSignup';

type TermsAndConditionsCoupon = {
  enabled: boolean;
  readonly: boolean;
  couponCode: string | null | undefined;
};

type TermsAndConditionsLoaderProps = {
  context: AppRouterContext & { coupon: TermsAndConditionsCoupon };
  deps: { couponCode: string | undefined };
  preload: boolean;
  cause: 'preload' | 'enter' | 'stay';
};

export type CouponSearchParamResolution =
  | { action: 'keep' }
  | { action: 'restore'; couponCode: string }
  | { action: 'strip' };

type ResolveCouponSearchParamOptions = {
  /** Same semantics as the route match's own `cause`. */
  cause: 'preload' | 'enter' | 'stay';
  /** Raw `ssu_coupon` value: `undefined` means the URL says nothing yet, `''` is the explicit removal marker. */
  searchParamCouponCode: string | undefined;
  searchParamCouponEligibilityResponse: CouponEligibility | null | undefined;
  oppCouponEligibilityResponse: CouponEligibility | null | undefined;
  couponCodeFromOpp: string | null | undefined;
};

/**
 * The single decision point for the `ssu_coupon` search param, using the
 * Opp's own persisted coupon as a fallback. Covers both syncing it into a
 * URL that says nothing about coupons yet (only on a fresh `enter`, so a
 * same-route Remove navigate can never be undone) and correcting a URL that
 * already carries an explicit `not_found` code. A BFF `not_found` rejection
 * is never an error to recover from — it's a signal that the client's
 * URL/persisted state disagrees with the BFF's current truth, so this always
 * refreshes the search param toward that truth rather than leaving a
 * known-stale code in place.
 */
function resolveCouponSearchParam({
  cause,
  searchParamCouponCode,
  searchParamCouponEligibilityResponse,
  oppCouponEligibilityResponse,
  couponCodeFromOpp,
}: ResolveCouponSearchParamOptions): CouponSearchParamResolution {
  const fallbackToOpp = (
    giveUp: CouponSearchParamResolution,
  ): CouponSearchParamResolution => {
    if (!couponCodeFromOpp) return giveUp;
    if (oppCouponEligibilityResponse?.status === 'not_found') return giveUp;

    return { action: 'restore', couponCode: couponCodeFromOpp };
  };

  if (searchParamCouponCode === undefined) {
    if (cause !== 'enter') return { action: 'keep' };

    return fallbackToOpp({ action: 'keep' });
  }

  if (searchParamCouponEligibilityResponse?.status !== 'not_found') {
    return { action: 'keep' };
  }

  return fallbackToOpp({ action: 'strip' });
}

export async function termsAndConditionsLoader({
  context,
  deps,
  preload,
  cause,
}: TermsAndConditionsLoaderProps) {
  const { enabled, readonly, couponCode: couponCodeFromOpp } = context.coupon;

  if (!enabled) return;

  // Read-only never live-validates: both fetches short-circuit to `undefined`.
  const searchParamCouponCode = readonly ? undefined : deps.couponCode;
  const oppCouponCode = readonly ? undefined : couponCodeFromOpp ?? undefined;

  // Query dedupes concurrent fetches sharing a queryKey, so this stays a
  // plain Promise.all even when both codes are the same.
  const [searchParamCouponEligibilityResponse, oppCouponEligibilityResponse] =
    await Promise.all([
      context.queryClient.ensureQueryData(
        getCouponEligibilityQueryOptions(searchParamCouponCode),
      ),
      context.queryClient.ensureQueryData(
        getCouponEligibilityQueryOptions(oppCouponCode),
      ),
    ]);

  if (preload || readonly) return;

  const couponSearchParam = resolveCouponSearchParam({
    cause,
    searchParamCouponCode,
    searchParamCouponEligibilityResponse,
    oppCouponEligibilityResponse,
    couponCodeFromOpp: oppCouponCode,
  });

  if (couponSearchParam.action === 'keep') return;

  throw redirect({
    to: '.',
    search: prev => ({
      ...prev,
      ssu_coupon:
        couponSearchParam.action === 'restore'
          ? couponSearchParam.couponCode
          : STRIP_COUPON,
    }),
    replace: true,
  });
}
